import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { fetchConfig } from '@theme/utilities';

class CrossSellProductForm {
  /** @type {(() => void) | undefined} */
  #onSuccess;

  /**
   * @param {HTMLElement} element
   * @param {{ onSuccess?: () => void }} [options]
   */
  constructor(element, options = {}) {
    this.element = element;
    this.#onSuccess = options.onSuccess;
    this.variants = [];
    this.splitSize = element.hasAttribute('data-cross-sell-split-size');
    /** @type {HTMLFormElement | null} */
    this.form = null;
    /** @type {HTMLSelectElement[]} */
    this.optionInputs = [];
    /** @type {HTMLInputElement | null} */
    this.variantInput = null;
    /** @type {HTMLButtonElement | null} */
    this.submitButton = null;
    /** @type {HTMLElement | null} */
    this.priceElement = null;
    /** @type {HTMLElement | null} */
    this.comparePriceElement = null;

    this.#refreshFromDom();
  }

  /**
   * Re-query live nodes. Cart morphs can replace form internals while keeping the item wrapper.
   */
  #refreshFromDom() {
    this.form = /** @type {HTMLFormElement | null} */ (this.element.querySelector('.cart-drawer-cross-sell__form'));
    this.optionInputs = Array.from(
      this.element.querySelectorAll('[data-cross-sell-option-index], [data-size-axis]'),
    ).filter((input) => input instanceof HTMLSelectElement);

    const variantInput = this.element.querySelector('[data-cross-sell-variant-id]');
    this.variantInput = variantInput instanceof HTMLInputElement ? variantInput : null;

    const submitButton = this.element.querySelector('[data-cross-sell-submit]');
    this.submitButton = submitButton instanceof HTMLButtonElement ? submitButton : null;

    const priceElement = this.element.querySelector('[data-cross-sell-price]');
    this.priceElement = priceElement instanceof HTMLElement ? priceElement : null;

    const comparePriceElement = this.element.querySelector('[data-cross-sell-compare-price]');
    this.comparePriceElement = comparePriceElement instanceof HTMLElement ? comparePriceElement : null;

    this.splitSize = this.element.hasAttribute('data-cross-sell-split-size');
    this.variants = this.#parseVariants();
  }

  #parseVariants() {
    const variantsScript = this.element.querySelector('[data-cross-sell-variants]');
    if (!(variantsScript instanceof HTMLScriptElement)) return [];

    try {
      return JSON.parse(variantsScript.textContent || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Shopify Liquid variant JSON may expose `options[]` and/or `option1`/`option2`/`option3`.
   * @param {object} variant
   * @param {number} zeroBasedIndex
   * @returns {string | null}
   */
  #getVariantOptionValue(variant, zeroBasedIndex) {
    if (Array.isArray(variant.options) && variant.options[zeroBasedIndex] != null) {
      return String(variant.options[zeroBasedIndex]);
    }

    const keyed = variant[`option${zeroBasedIndex + 1}`];
    return keyed == null ? null : String(keyed);
  }

  /**
   * Prefer a size match that keeps non-size options (e.g. Color) from the current variant.
   * Falls back to an available size match, then any size match.
   * Without this, Color+Size products resolve to the first color in variant order — often a
   * fully sold-out colorway — so every waist/length looks sold out.
   * @param {number} optionPosition - Zero-based size option index
   * @param {(variant: object) => boolean} matchesSize
   * @returns {object | undefined}
   */
  #resolveVariantForSize(optionPosition, matchesSize) {
    const currentId = this.variantInput?.value;
    const currentVariant = this.variants.find((variant) => String(variant.id) === String(currentId));

    if (currentVariant) {
      const preferred = this.variants.find((variant) => {
        if (!matchesSize(variant)) return false;

        for (let index = 0; index < 3; index++) {
          if (index === optionPosition) continue;

          const currentValue = this.#getVariantOptionValue(currentVariant, index);
          const nextValue = this.#getVariantOptionValue(variant, index);

          if (currentValue != null && nextValue != null && currentValue !== nextValue) {
            return false;
          }
        }

        return true;
      });

      // Keep the locked colorway when it has this size (available or not).
      if (preferred?.available) return preferred;

      // If the locked colorway is fully OOS, prefer another color with stock for this size.
      const lockedColorHasStock = this.variants.some((variant) => {
        if (!variant.available || variant.isHiddenSize) return false;

        for (let index = 0; index < 3; index++) {
          if (index === optionPosition) continue;

          const currentValue = this.#getVariantOptionValue(currentVariant, index);
          const nextValue = this.#getVariantOptionValue(variant, index);

          if (currentValue != null && nextValue != null && currentValue !== nextValue) {
            return false;
          }
        }

        return true;
      });

      if (preferred && lockedColorHasStock) return preferred;
    }

    return (
      this.variants.find((variant) => matchesSize(variant) && variant.available) ||
      this.variants.find(matchesSize)
    );
  }

  /**
   * @returns {object | undefined}
   */
  #findSingleOptionVariant() {
    const selectedOptionInput = this.optionInputs.find((input) => input.hasAttribute('data-cross-sell-option-index'));
    if (!(selectedOptionInput instanceof HTMLSelectElement)) return undefined;

    const selectedOptionValue = selectedOptionInput.value;
    const optionPosition = Number.parseInt(selectedOptionInput.dataset.crossSellOptionPosition || '0', 10);

    const matchesSize = (variant) =>
      !variant.isHiddenSize && this.#getVariantOptionValue(variant, optionPosition) === selectedOptionValue;

    return this.#resolveVariantForSize(optionPosition, matchesSize);
  }

  /**
   * @returns {object | undefined}
   */
  #findSplitSizeVariant() {
    const waistSelect = this.element.querySelector('[data-size-axis="waist"]');
    const lengthSelect = this.element.querySelector('[data-size-axis="length"]');

    if (!(waistSelect instanceof HTMLSelectElement) || !(lengthSelect instanceof HTMLSelectElement)) {
      return undefined;
    }

    const waist = waistSelect.value.trim();
    const length = lengthSelect.value.trim();
    const optionPosition = Number.parseInt(waistSelect.dataset.crossSellOptionPosition || '0', 10);

    const matchesSize = (variant) => {
      if (variant.isHiddenSize) return false;

      const optionValue = this.#getVariantOptionValue(variant, optionPosition);
      if (optionValue == null) return false;

      const parts = optionValue.split('/').map((part) => part.trim());
      return parts[0] === waist && parts[1] === length;
    };

    return this.#resolveVariantForSize(optionPosition, matchesSize);
  }

  syncVariant() {
    this.#refreshFromDom();
    this.#syncOptionWidths();

    if (this.optionInputs.length === 0 || this.variants.length === 0) return;

    const matchedVariant = this.splitSize ? this.#findSplitSizeVariant() : this.#findSingleOptionVariant();

    if (!matchedVariant) {
      if (this.variantInput instanceof HTMLInputElement) {
        this.variantInput.disabled = true;
      }

      if (this.submitButton instanceof HTMLButtonElement) {
        this.submitButton.disabled = true;
        this.submitButton.textContent = this.element.dataset.crossSellSoldOutLabel || '';
      }

      this.#syncPdpLinks(this.variantInput?.value);
      return;
    }

    if (this.variantInput instanceof HTMLInputElement) {
      this.variantInput.value = String(matchedVariant.id);
      this.variantInput.disabled = !matchedVariant.available;
    }

    if (this.submitButton instanceof HTMLButtonElement) {
      this.submitButton.disabled = !matchedVariant.available;
      this.submitButton.textContent = matchedVariant.available
        ? this.element.dataset.crossSellAddLabel || ''
        : this.element.dataset.crossSellSoldOutLabel || '';
    }

    this.#syncPdpLinks(matchedVariant.id);

    if (this.priceElement instanceof HTMLElement && typeof matchedVariant.price === 'number') {
      this.priceElement.textContent = this.#formatMoney(matchedVariant.price);
    }

    if (
      this.comparePriceElement instanceof HTMLElement &&
      typeof matchedVariant.compare_at_price === 'number' &&
      matchedVariant.compare_at_price > matchedVariant.price
    ) {
      this.comparePriceElement.textContent = this.#formatMoney(matchedVariant.compare_at_price);
      this.comparePriceElement.hidden = false;
    } else if (this.comparePriceElement instanceof HTMLElement) {
      this.comparePriceElement.hidden = true;
    }
  }

  /**
   * Keep panel image/title links pointed at the currently matched variant.
   * Media sits outside `[data-cross-sell-item]`, so resolve from the panel root.
   * @param {string | number | null | undefined} variantId
   */
  #syncPdpLinks(variantId) {
    const panel = this.element.closest('[data-cross-sell-panel]');
    if (!(panel instanceof HTMLElement)) return;

    for (const link of panel.querySelectorAll('[data-cross-sell-pdp-link]')) {
      if (!(link instanceof HTMLAnchorElement) || !link.href) continue;

      try {
        const url = new URL(link.href, window.location.origin);
        if (variantId) {
          url.searchParams.set('variant', String(variantId));
        }
        link.href = url.href;
      } catch {
        // ignore invalid hrefs
      }
    }
  }

  #syncOptionWidths() {
    for (const input of this.optionInputs) {
      this.#syncOptionWidth(input);
    }
  }

  /**
   * Match cart-items size select width: CSS var from selected label length.
   * @param {HTMLSelectElement} input
   */
  #syncOptionWidth(input) {
    const selectedOption = input.options[input.selectedIndex];
    const selectedLabel = selectedOption?.textContent?.trim() || input.value || '';
    const widthEm = Math.max(selectedLabel.length * 0.65, 1);

    input.style.setProperty('--cross-sell-size-ch', `${widthEm}em`);
    input.style.removeProperty('width');
  }

  #formatMoney(value) {
    const moneyFormat = this.element.dataset.crossSellMoneyFormat || '{{amount}}';
    const currency = this.element.dataset.crossSellCurrency || 'EUR';
    return formatMoney(value, moneyFormat, currency);
  }

  addToCart = async () => {
    // Always resolve from the live select values right before submit.
    this.syncVariant();

    if (!(this.form instanceof HTMLFormElement)) return;
    if (!(this.variantInput instanceof HTMLInputElement)) return;
    if (!(this.submitButton instanceof HTMLButtonElement)) return;
    if (this.submitButton.disabled || this.variantInput.disabled) return;

    const formData = new FormData(this.form);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    for (const cartItemsComponent of cartItemsComponents) {
      if (!(cartItemsComponent instanceof HTMLElement) || !cartItemsComponent.dataset.sectionId) continue;
      sectionIds.push(cartItemsComponent.dataset.sectionId);
    }

    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    this.submitButton.disabled = true;

    try {
      const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(cartAddUrl, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        this.submitButton.disabled = this.variantInput.disabled;
        return;
      }

      this.#onSuccess?.();

      const productId = this.element.dataset.crossSellProductId || '';
      document.dispatchEvent(
        new CartAddEvent({}, `cart-drawer-cross-sell-${productId}`, {
          source: 'product-form-component',
          itemCount: 1,
          productId,
          sections: data.sections || {},
        }),
      );
    } catch {
      this.submitButton.disabled = this.variantInput.disabled;
      return;
    }

    this.submitButton.disabled = this.variantInput.disabled;
  };
}

class CartDrawerCrossSell extends Component {
  #abortController = new AbortController();
  #forms = [];
  /** @type {HTMLElement | null} */
  #cardsElement = null;
  #isDraggingCards = false;
  #didDragCards = false;
  #dragStartX = 0;
  #dragStartScrollLeft = 0;

  connectedCallback() {
    super.connectedCallback();
    // Morph can disconnect/reconnect this host. A prior abort leaves the old
    // signal dead, so listeners must bind to a fresh controller each connect.
    this.#abortController = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#abortController.signal });
    this.addEventListener('change', this.#handleOptionChange, { signal: this.#abortController.signal });
    this.addEventListener('submit', this.#handleFormSubmit, {
      capture: true,
      signal: this.#abortController.signal,
    });
    this.#refreshAfterMorph();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#teardownCardsDrag();
    this.#forms = [];
  }

  /**
   * Section morph reuses this host and updates children without a full
   * reconnect — refresh drag targets, forms, and clear sticky drag state.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#refreshAfterMorph();
  }

  #refreshAfterMorph() {
    this.#didDragCards = false;
    this.#isDraggingCards = false;
    this.#teardownCardsDrag();
    this.#initForms();
    this.#initCardsDrag();
    this.#closeAll();
  }

  #initForms() {
    this.#forms = this.#forms.filter((form) => form.element.isConnected);

    const items = this.querySelectorAll('[data-cross-sell-item]');

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;

      const existing = this.#forms.find((entry) => entry.element === item);
      if (existing) {
        existing.syncVariant();
        continue;
      }

      const form = new CrossSellProductForm(item, {
        onSuccess: () => this.#closeAll(true),
      });
      form.syncVariant();
      this.#forms.push(form);
    }
  }

  /**
   * @param {HTMLElement} item
   * @returns {CrossSellProductForm | undefined}
   */
  #getFormForItem(item) {
    this.#initForms();
    return this.#forms.find((entry) => entry.element === item);
  }

  /**
   * @param {Event} event
   */
  #handleOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.matches('[data-cross-sell-option-index], [data-size-axis]')) return;

    const item = target.closest('[data-cross-sell-item]');
    if (!(item instanceof HTMLElement)) return;

    this.#getFormForItem(item)?.syncVariant();
  };

  /**
   * Block native product-form POSTs so Shopify never navigates to /cart.
   * @param {Event} event
   */
  #handleFormSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.classList.contains('cart-drawer-cross-sell__form')) return;

    event.preventDefault();
    event.stopPropagation();
  };

  #initCardsDrag() {
    const cards = this.querySelector('.cart-drawer-cross-sell__cards');
    if (!(cards instanceof HTMLElement)) return;

    this.#cardsElement = cards;
    cards.addEventListener('pointerdown', this.#handleCardsDragStart, { signal: this.#abortController.signal });
  }

  #teardownCardsDrag() {
    this.#isDraggingCards = false;
    this.#didDragCards = false;
    this.#cardsElement?.classList.remove('is-dragging');
    this.#cardsElement?.removeEventListener('pointerdown', this.#handleCardsDragStart);
    this.#cardsElement = null;
    document.removeEventListener('pointermove', this.#handleCardsDragMove);
    document.removeEventListener('pointerup', this.#handleCardsDragEnd);
    document.removeEventListener('pointercancel', this.#handleCardsDragEnd);
  }

  /**
   * @param {PointerEvent} event
   */
  #handleCardsDragStart = (event) => {
    const cards = this.#cardsElement;
    if (!cards) return;
    if (event.button !== 0) return;
    if (event.pointerType === 'touch') return;

    this.#isDraggingCards = true;
    this.#didDragCards = false;
    this.#dragStartX = event.pageX;
    this.#dragStartScrollLeft = cards.scrollLeft;

    document.addEventListener('pointermove', this.#handleCardsDragMove);
    document.addEventListener('pointerup', this.#handleCardsDragEnd);
    document.addEventListener('pointercancel', this.#handleCardsDragEnd);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleCardsDragMove = (event) => {
    const cards = this.#cardsElement;
    if (!cards || !this.#isDraggingCards) return;

    const deltaX = event.pageX - this.#dragStartX;

    if (!this.#didDragCards) {
      if (Math.abs(deltaX) <= 5) return;

      this.#didDragCards = true;
      cards.classList.add('is-dragging');
    }

    cards.scrollLeft = this.#dragStartScrollLeft - deltaX;
    event.preventDefault();
  };

  #handleCardsDragEnd = () => {
    const cards = this.#cardsElement;
    if (!cards) return;

    this.#isDraggingCards = false;
    cards.classList.remove('is-dragging');
    document.removeEventListener('pointermove', this.#handleCardsDragMove);
    document.removeEventListener('pointerup', this.#handleCardsDragEnd);
    document.removeEventListener('pointercancel', this.#handleCardsDragEnd);

    // Browsers often skip the click after a real drag, which would leave
    // #didDragCards stuck and swallow the next click (e.g. Add to cart).
    if (this.#didDragCards) {
      setTimeout(() => {
        this.#didDragCards = false;
      }, 0);
    }
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (this.#didDragCards) {
      const clickedInCards = Boolean(event.target.closest('.cart-drawer-cross-sell__cards'));
      this.#didDragCards = false;

      if (clickedInCards) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }

    const submitButton = event.target.closest('[data-cross-sell-submit]');
    if (submitButton) {
      event.preventDefault();
      const item = submitButton.closest('[data-cross-sell-item]');
      if (!(item instanceof HTMLElement)) return;

      this.#getFormForItem(item)?.addToCart();
      return;
    }

    const closeButton = event.target.closest('[data-cross-sell-close]');
    if (closeButton) {
      event.preventDefault();
      this.#closeAll();
      return;
    }

    const trigger = /** @type {HTMLElement | null} */ (event.target.closest('[data-cross-sell-trigger]'));
    if (!trigger) return;

    event.preventDefault();

    const productId = trigger.dataset.crossSellTrigger;
    if (!productId) return;

    const isActive = trigger.hasAttribute('data-active');
    if (isActive) {
      this.#closeAll();
      return;
    }

    this.#closeAll();

    const panel = this.querySelector(`[data-cross-sell-panel="${productId}"]`);
    if (!(panel instanceof HTMLElement)) return;

    trigger.setAttribute('data-active', '');
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    this.#header?.classList.add('cross-sell-open');

    const item = panel.querySelector('[data-cross-sell-item]');
    if (item instanceof HTMLElement) {
      this.#getFormForItem(item)?.syncVariant();
    }
  };

  get #header() {
    return this.closest('.cart-items-component')?.querySelector('.cart-drawer__header') ?? null;
  }

  #closeAll(instant = false) {
    const triggers = this.querySelectorAll('[data-cross-sell-trigger]');
    const panels = this.querySelectorAll('[data-cross-sell-panel]');

    for (const trigger of triggers) {
      if (!(trigger instanceof HTMLElement)) continue;
      trigger.removeAttribute('data-active');
      trigger.setAttribute('aria-expanded', 'false');
    }

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = true;
    }

    const header = this.#header;
    header?.classList.remove('cross-sell-open');
    if (instant) {
      header?.getAnimations({ subtree: true }).forEach((a) => a.finish());
    }
  }
}

if (!customElements.get('cart-drawer-cross-sell')) {
  customElements.define('cart-drawer-cross-sell', CartDrawerCrossSell);
}
